import React from 'react';
import Header from './Header';
import Footer from './Footer';
import { FaCheck, FaStore, FaRecycle, FaChargingStation, FaChartLine } from "react-icons/fa";
import whatwedo from '../assets/whatwedo.png';
import howwework from '../assets/howwework.png';
import getinvolved from '../assets/getinvolved.png';
import aboutus from '../assets/aboutus.png';

const About = () => {
   return (
      <div className="bg-[#f9fdf7] min-h-screen">
         <Header />

         <div className="max-w-7xl mx-auto px-4 py-12 md:flex md:items-center md:gap-12 ">
            {/* Image Section */}
            <div className="w-full md:w-1/2 mb-8 md:mb-0 flex justify-center">
               <img
                  src={aboutus}
                  alt="About Us"
                  className="max-w-full h-auto object-cover rounded-lg shadow-lg w-[100%] md:h-[350px]"
               />
            </div>

            {/* Text Content */}
            <div className="w-full md:w-1/2">
               <p className="text-sm font-semibold text-[#1a4632] mb-2">About Us</p>
               <h2 className="text-3xl md:text-4xl font-semibold text-[#9cc026] mb-4 leading-snug">
                  Turning Bottles into Miles, and Miles into a Cleaner Future
               </h2>
               <p className="text-gray-600 text-base leading-relaxed">
                  FareFuel is a New York-based eco-rewards platform transforming how people connect recycling with everyday mobility. Built on the principles of sustainability and accessibility, we help individuals—especially taxi and rideshare drivers—turn everyday recyclables into useful rewards like fuel savings and EV charging credits.
               </p>
            </div>
         </div>

         <section className="bg-[#1a4632] py-16">
            <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row md:items-start md:justify-between gap-8">
               {/* Heading */}
               <div className="md:w-1/3">
                  <h3 className="text-[#9cc026] text-3xl md:text-4xl font-semibold">
                     What We Do
                  </h3>

                  {/* Image Below Heading */}
                  <div className="mt-4">
                     <img
                        src={whatwedo}
                        alt="What We Do illustration"
                        className="w-full md:w-64 h-auto rounded-lg shadow-md border border-gray-200"
                     />
                  </div>
               </div>

               {/* Copy */}
               <div className="text-white md:w-2/3 text-base md:text-lg leading-relaxed space-y-4">
                  <p>
                     At FareFuel, we connect the dots between the New York State Bottle Bill,
                     eco-conscious behavior, and everyday mobility. Through our network of
                     recycling kiosks, smart bins, and retail partners, we make it easy to:
                  </p>

                  <ul className="list-disc list-inside space-y-2">
                     <li>Recycle eligible beverage containers with NY 5¢ deposit labels</li>
                     <li>Use your rewards for fuel savings or EV charging</li>
                     <li>Support clean transportation while earning useful rewards</li>
                  </ul>

                  <p>
                     We accept all containers covered under the Bottle Bill, including those marked
                     <strong> CTRV:ME NY</strong>, provided they were purchased in New York and bear
                     the official “NY 5¢” label.
                  </p>
               </div>
            </div>
         </section>

         <section className="bg-[#f9fdf7] py-16">
            <div className="max-w-7xl mx-auto px-4 flex flex-col lg:flex-row items-start gap-6 lg:gap-10">
               {/* Left: Heading and List */}
               <div className="lg:w-2/3">
                  <h3 className="text-3xl md:text-4xl font-semibold text-[#9cc026] mb-6">
                     How It Works
                  </h3>
                  <ul className="space-y-4 mb-10">
                     <li className="flex items-start text-gray-700">
                        <div className="min-w-[1.25rem] mt-1 mr-3">
                           <FaCheck className="text-green-500 text-lg" />
                        </div>
                        <span>Collect your bottles and cans with NY deposit labels</span>
                     </li>

                     <li className="flex items-start text-gray-700">
                        <div className="min-w-[1.25rem] mt-1 mr-3">
                           <FaCheck className="text-green-500 text-lg" />
                        </div>
                        <span>Drop them off at a FareFuel partner or reverse vending machine (RVM)</span>
                     </li>

                     <li className="flex items-start text-gray-700">
                        <div className="min-w-[1.25rem] mt-1 mr-3">
                           <FaCheck className="text-green-500 text-lg" />
                        </div>
                        <span>Earn rewards per container — 5¢ each</span>
                     </li>

                     <li className="flex items-start text-gray-700">
                        <div className="min-w-[1.25rem] mt-1 mr-3">
                           <FaCheck className="text-green-500 text-lg" />
                        </div>
                        <span>
                           Use your rewards for fuel savings or EV charging credits
                        </span>
                     </li>
                  </ul>
               </div>

               {/* Right: Image */}
               <div className="w-full lg:w-1/3 flex justify-center">
                  <img
                     src={howwework}
                     alt="Illustration of the bottle recycling process"
                     className="w-full max-w-[18rem] sm:max-w-[20rem] lg:w-60 lg:sm:w-72 h-auto object-contain"
                  />
               </div>

            </div>

            {/* Transparent Mission Card */}
            <div className="bg-[#1a4632]/40 backdrop-blur-md p-6 mt-12 rounded-lg shadow-lg border border-white/20 text-white w-[90%] sm:w-full max-w-5xl mx-auto">
               <h4 className="text-2xl font-semibold mb-2 text-white">Our Mission</h4>
               <p>
                  To create a circular, inclusive economy where waste becomes wealth and every ride
                  leaves the city greener than it found it.
               </p>
            </div>
         </section>


         <section className="bg-[#eaf3dd] py-16">
            <div className="max-w-7xl mx-auto px-4 md:px-8">
               <h2 className="text-3xl md:text-4xl font-semibold text-[#1a4632] mb-10 text-center">
                  Our Network
               </h2>

               <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                  {/* Left Column - Partner Types */}
                  <ul className="space-y-6 text-gray-700 text-lg">
                     <li className="flex items-center">
                        <FaStore className="text-[#9cc026] text-xl mr-3" />
                        Local retailers and gas stations
                     </li>
                     <li className="flex items-center">
                        <FaRecycle className="text-[#9cc026] text-xl mr-3" />
                        Recycling centers and vending machines
                     </li>
                     <li className="flex items-center">
                        <FaChargingStation className="text-[#9cc026] text-xl mr-3" />
                        EV charging providers
                     </li>
                     <li className="flex items-center">
                        <FaChartLine className="text-[#9cc026] text-xl mr-3" />
                        Digital platforms supporting rewards and mobility services
                     </li>
                  </ul>

                  {/* Right Column - Summary */}
                  <div className="bg-[#f9fdf7] border-l-4 border-[#9cc026] p-6 rounded-lg shadow-md flex items-center text-gray-500">
                     <p className="font-bold italic text-2xl md:text-xl leading-relaxed">
                        Together, we’re building an ecosystem of clean energy, sustainable transportation,
                        and everyday savings—one bottle at a time.
                     </p>
                  </div>
               </div>
            </div>
         </section>

         <section className="bg-[#f9fdf7] py-20 px-6">
            <div className="max-w-5xl mx-auto text-center">
               {/* Tagline / Intro */}
               <p className="text-[#1a4632] text-lg font-medium tracking-wide italic mb-4">
                  Whether you're powering a yellow cab or charging a zero-emission vehicle, every bottle counts.
               </p>

               {/* Heading */}
               <h1 className="text-3xl md:text-3xl font-bold text-[#9cc026] leading-tight mb-3">
                  Why Taxi & Rideshare Drivers?
               </h1>

               {/* Divider */}
               <div className="w-16 h-1 bg-[#9cc026] mx-auto rounded-full mb-6" />

               {/* Main Description */}
               <p className="text-gray-700 text-base md:text-lg leading-relaxed max-w-3xl mx-auto mb-6">
                  New York City drivers are at the heart of urban mobility. FareFuel helps them save on everyday fuel and charging,
                  adopt more sustainable habits, and contribute to a cleaner environment with every trip.
               </p>

               {/* Emphasis Statement */}
               <p className="text-[#1a4632] font-semibold text-lg md:text-xl max-w-2xl mx-auto">
                  For many, our program offers meaningful savings along with environmental benefits.
               </p>
            </div>
         </section>


         <section className="bg-[#1a4632] py-16 text-white px-4">
            <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-10">
               <div className="md:w-1/2 text-center md:text-left">
                  <h2 className="text-3xl md:text-4xl font-semibold mb-4 text-[#9cc026]">
                     Get Involved
                  </h2>
                  <p className="text-lg md:text-xl mb-4">
                     Are you a driver, retailer, property manager, or city partner?
                  </p>
                  <p className="text-base md:text-lg mb-6">
                     Contact us to learn how you can join the FareFuel movement and bbe part of a cleaner, more sustainable future.
                  </p>
                  <a
                     href="#contact"
                     className="inline-block px-6 py-2 bg-green-600 text-white font-semibold rounded-3xl shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400 transition"
                  >
                     Contact Us
                  </a>
               </div>

               {/* Right Side Image */}
               <div className="md:w-1/2 flex justify-center">
                  <img
                     src={getinvolved}
                     alt="People joining recycling and clean energy program"
                     className="w-64 h-auto object-contain"
                  />
               </div>
            </div>
         </section>

         <Footer />
      </div>
   );
};

export default About;
